import { useEffect, useState } from 'react';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../contexts/AuthContext';

// Game content checklist for Padelante tours — the shot list marketing needs
// captured around a padel game. Lives alongside the social media plan in
// requests/{requestId}/socialMediaPlan/gameContent, with one entry per item id
// so staff, marketing and the assigned tour leader all tick the same list.
const CHECKLIST_DOC_ID = 'gameContent';

const ITEMS = [
  { id: 1,  label: 'Player headshot',                          quantity: '1 per player', when: 'Before game',        type: 'Photo', required: true },
  { id: 2,  label: 'Full player group photo',                  quantity: '1',            when: 'Before game',        type: 'Photo', required: true },
  { id: 3,  label: 'Venue / court photo',                      quantity: '1',            when: 'Before game',        type: 'Photo', required: true },
  { id: 4,  label: 'Warm-up photos',                           quantity: '2',            when: 'Before game',        type: 'Photo', required: true },
  { id: 5,  label: 'Before-game team video',                   quantity: '1',            when: 'Before game',        type: 'Video', required: true },
  { id: 6,  label: 'Action shots during the game',             quantity: '4',            when: 'During game',        type: 'Photo', required: true },
  { id: 7,  label: 'Action video clips',                       quantity: '4',            when: 'During game',        type: 'Video', required: true },
  { id: 8,  label: 'Close-up detail shots — ball, racket, feet', quantity: '3',           when: 'During game',        type: 'Photo', required: false },
  { id: 9,  label: 'Player reactions / celebrations',          quantity: '2',            when: 'During / after game', type: 'Photo', required: true },
  { id: 10, label: 'Score / result photo',                     quantity: '1',            when: 'After game',         type: 'Photo', required: false },
  { id: 11, label: 'After-game group photo',                   quantity: '1',            when: 'After game',         type: 'Photo', required: true },
  { id: 12, label: 'Player video testimonial',                 quantity: '1 per player', when: 'After game',         type: 'Video', required: true },
  { id: 13, label: 'Coach / tour operator testimonial',        quantity: '1',            when: 'After game',         type: 'Video', required: false },
  { id: 14, label: 'Final wrap-up video',                      quantity: '1',            when: 'After game',         type: 'Video', required: true },
];

// Phases in running order, so the list reads the way the day actually goes.
const PHASES = ['Before game', 'During game', 'During / after game', 'After game'];

function formatDate(ts) {
  if (!ts?.seconds) return '';
  return new Date(ts.seconds * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

export default function GameContentChecklist({ requestId }) {
  const { currentUser } = useAuth();
  const [items, setItems] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'requests', requestId, 'socialMediaPlan', CHECKLIST_DOC_ID),
      (snap) => {
        setItems(snap.exists() ? (snap.data().items || {}) : {});
        setLoading(false);
      },
      (err) => {
        console.error('Game content checklist:', err);
        setLoading(false);
      }
    );
    return unsub;
  }, [requestId]);

  async function toggle(item) {
    const nowDone = !items[item.id]?.done;
    setSaving(item.id);
    setError('');
    try {
      await setDoc(
        doc(db, 'requests', requestId, 'socialMediaPlan', CHECKLIST_DOC_ID),
        {
          items: {
            [item.id]: nowDone
              ? { done: true, doneAt: serverTimestamp(), doneByEmail: currentUser.email }
              : { done: false },
          },
          updatedAt: serverTimestamp(),
          updatedByEmail: currentUser.email,
        },
        { merge: true }
      );
    } catch (err) {
      console.error('Checklist toggle failed:', err);
      setError('Could not save that tick. Please try again.');
    } finally {
      setSaving(null);
    }
  }

  if (loading) {
    return <p className="text-[13px] text-[#9CA3AF]">Loading…</p>;
  }

  const doneCount = ITEMS.filter((i) => items[i.id]?.done).length;
  const requiredOutstanding = ITEMS.filter((i) => i.required && !items[i.id]?.done).length;
  const pct = Math.round((doneCount / ITEMS.length) * 100);

  return (
    <div className="space-y-4">
      {/* Progress */}
      <div className="rounded-[10px] bg-surface border border-[#F3F4F6] px-3 py-2.5">
        <div className="flex items-center justify-between gap-3 mb-2">
          <p className="text-[12.5px] font-semibold text-[#111827]">
            {doneCount} / {ITEMS.length} captured
          </p>
          <p className="text-[11.5px] text-[#9CA3AF]">
            {requiredOutstanding === 0
              ? 'All required items done'
              : `${requiredOutstanding} required item${requiredOutstanding === 1 ? '' : 's'} outstanding`}
          </p>
        </div>
        <div className="w-full bg-[#E5E7EB] rounded-full h-1.5">
          <div
            className={`h-1.5 rounded-full transition-all duration-200 ${doneCount === ITEMS.length ? 'bg-sg' : 'bg-pb'}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      {error && <p className="text-[12px] text-dr">{error}</p>}

      {PHASES.map((phase) => {
        const phaseItems = ITEMS.filter((i) => i.when === phase);
        if (phaseItems.length === 0) return null;
        return (
          <div key={phase}>
            <p className="text-[11px] font-semibold text-[#9CA3AF] uppercase tracking-[0.9px] mb-2">{phase}</p>
            <div className="space-y-2">
              {phaseItems.map((item) => {
                const state = items[item.id];
                const done = !!state?.done;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => toggle(item)}
                    disabled={saving === item.id}
                    className={`w-full text-left flex items-start gap-3 p-3 rounded-[8px] border transition disabled:opacity-60 ${
                      done
                        ? 'bg-green-light/40 border-sg/25'
                        : 'bg-surface border-[#F3F4F6] hover:border-pb/30'
                    }`}
                  >
                    <span
                      className={`w-[18px] h-[18px] mt-[1px] rounded-[5px] border-2 flex items-center justify-center flex-shrink-0 transition ${
                        done ? 'bg-sg border-sg' : 'bg-white border-[#D1D5DB]'
                      }`}
                    >
                      {done && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>

                    <span className="flex-1 min-w-0">
                      <span className={`block text-[13px] font-medium ${done ? 'text-[#6B7280] line-through' : 'text-[#111827]'}`}>
                        {item.label}
                      </span>
                      <span className="block text-[11.5px] text-[#9CA3AF] mt-0.5">
                        {item.quantity} · {item.type}
                        {!item.required && ' · Optional'}
                        {done && state?.doneByEmail && ` · ${formatDate(state.doneAt)} by ${state.doneByEmail}`}
                      </span>
                    </span>

                    {item.required && !done && (
                      <span className="text-[10px] font-bold uppercase tracking-[0.6px] text-ba bg-amber-light px-[7px] py-0.5 rounded-[20px] flex-shrink-0 mt-0.5">
                        Required
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
